import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { ResponsiveLayout } from '@/components/layout/ResponsiveLayout';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { AuthGuard } from '@/components/AuthGuard';
import { AdminGuard } from '@/components/AdminGuard';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { AppVersionWatcher } from '@/components/AppVersionWatcher';
import { DocumentTitle } from '@/components/DocumentTitle';



// Pages
import { LoginPage } from '@/pages/auth/LoginPage';
import { RegisterPage } from '@/pages/auth/RegisterPage';
import { DashboardPage } from '@/pages/dashboard/DashboardPage';
import { AlunosPage } from '@/pages/alunos/AlunosPage';
import { NovoAlunoPage } from '@/pages/alunos/NovoAlunoPage';
import { AlunoDetailPage } from '@/pages/alunos/AlunoDetailPage';
import { EmpresasPage } from '@/pages/empresas/EmpresasPage';
import { NovaEmpresaPage } from '@/pages/empresas/NovaEmpresaPage';
import { CidadesPage } from '@/pages/cidades/CidadesPage';
import { NovaCidadePage } from '@/pages/cidades/NovaCidadePage';
import { CursosPage } from '@/pages/cursos/CursosPage';
import { NovoCursoPage } from '@/pages/cursos/NovoCursoPage';
import { EstagiosPage } from '@/pages/alunos/estagios/EstagiosPage';
import { NovaEstagioPage } from '@/pages/alunos/estagios/NovaEstagioPage';
import { VerEstagioPage } from '@/pages/alunos/estagios/VerEstagioPage';
import { InstituicoesPage } from '@/pages/instituicoes/InstituicoesPage';
import { NovaInstituicaoPage } from '@/pages/instituicoes/NovaInstituicaoPage';
import { SeguradorasPage } from '@/pages/seguradoras/SeguradorasPage';
import { NovaSeguradoraPage } from '@/pages/seguradoras/NovaSeguradoraPage';
import { AgentesIntegradoresPage } from '@/pages/agentesIntegradores/AgentesIntegradoresPage';
import { NovoAgenteIntegradorPage } from '@/pages/agentesIntegradores/NovoAgenteIntegradorPage';
import { HorariosPage } from '@/pages/horarios/HorariosPage';
import { NovoHorarioPage } from '@/pages/horarios/NovoHorarioPage';
import { VerHorariosEstagioPage } from '@/pages/horarios/VerHorariosEstagioPage';
import { RelatoriosPage } from '@/pages/relatorios/RelatoriosPage';
import { NovoRelatorioPage } from '@/pages/relatorios/NovoRelatorioPage';
import { RelatoriosPersonalizadosPage } from '@/pages/relatorios/RelatoriosPersonalizadosPage';
import { GerenciarRelatoriosPage } from '@/pages/relatorios/GerenciarRelatoriosPage';
import { TermoContratoPage } from '@/pages/termos/TermoContratoPage';
import { TermoRescisaoPage } from '@/pages/termos/TermoRescisaoPage';
import { ConfiguracaoTermoPage } from '@/pages/configuracaoTermo/ConfiguracaoTermoPage';
import { PerfilPage } from '@/pages/perfil/PerfilPage';
import { AdminPlanosPage } from '@/pages/admin/AdminPlanosPage';
import { AdminUsuariosPage } from '@/pages/admin/AdminUsuariosPage';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
    },
  },
});

// Protected Routes Component — painel admin em layout próprio (/admin), restante no layout do sistema
const ProtectedRoutes: React.FC = () => {
  return (
    <AuthGuard>
      <Routes>
        <Route path="/admin/login" element={<Navigate to="/admin/planos" replace />} />

        <Route
          path="/admin"
          element={
            <AdminGuard>
              <AdminLayout />
            </AdminGuard>
          }
        >
          <Route index element={<Navigate to="/admin/planos" replace />} />
          <Route path="planos" element={<AdminPlanosPage />} />
          <Route path="usuarios" element={<AdminUsuariosPage />} />
          <Route path="perfil" element={<PerfilPage />} />
        </Route>

        <Route path="/" element={<ResponsiveLayout />}>
          <Route index element={<DashboardPage />} />
          <Route path="alunos" element={<AlunosPage />} />
          <Route path="alunos/:id" element={<AlunoDetailPage />} />
          <Route path="alunos/novo" element={<NovoAlunoPage />} />
          <Route path="alunos/:id/editar" element={<NovoAlunoPage />} />
          <Route path="empresas" element={<EmpresasPage />} />
          <Route path="empresas/nova" element={<NovaEmpresaPage />} />
          <Route path="empresas/:id/editar" element={<NovaEmpresaPage />} />
          <Route path="cidades" element={<CidadesPage />} />
          <Route path="cidades/nova" element={<NovaCidadePage />} />
          <Route path="cidades/:id/editar" element={<NovaCidadePage />} />
          <Route path="cursos" element={<CursosPage />} />
          <Route path="cursos/novo" element={<NovoCursoPage />} />
          <Route path="cursos/:id/editar" element={<NovoCursoPage />} />
          <Route path="estagios" element={<EstagiosPage />} />
          <Route path="estagios/novo" element={<NovaEstagioPage />} />
          <Route path="estagios/:id" element={<VerEstagioPage />} />
          <Route path="estagios/:id/editar" element={<NovaEstagioPage />} />
          <Route path="instituicoes" element={<InstituicoesPage />} />
          <Route path="instituicoes/nova" element={<NovaInstituicaoPage />} />
          <Route path="instituicoes/:id/editar" element={<NovaInstituicaoPage />} />
          <Route path="seguradoras" element={<SeguradorasPage />} />
          <Route path="seguradoras/nova" element={<NovaSeguradoraPage />} />
          <Route path="seguradoras/:id/editar" element={<NovaSeguradoraPage />} />
          <Route path="agentes-integradores" element={<AgentesIntegradoresPage />} />
          <Route path="agentes-integradores/novo" element={<NovoAgenteIntegradorPage />} />
          <Route path="agentes-integradores/:id/editar" element={<NovoAgenteIntegradorPage />} />
          <Route path="horarios" element={<HorariosPage />} />
          <Route path="horarios/novo" element={<NovoHorarioPage />} />
          <Route path="horarios/:id/editar" element={<NovoHorarioPage />} />
          <Route path="horarios/estagio/:estagioId" element={<VerHorariosEstagioPage />} />
          <Route path="relatorios" element={<RelatoriosPage />} />
          <Route path="relatorios/novo" element={<NovoRelatorioPage />} />
          <Route path="relatorios/:id/editar" element={<NovoRelatorioPage />} />
          <Route path="relatorios/personalizados" element={<RelatoriosPersonalizadosPage />} />
          <Route path="relatorios/gerenciar" element={<GerenciarRelatoriosPage />} />
          <Route path="termo-contrato" element={<TermoContratoPage />} />
          <Route path="termo-rescisao" element={<TermoRescisaoPage />} />
          <Route path="configuracao-termo" element={<ConfiguracaoTermoPage />} />
          <Route path="perfil" element={<PerfilPage />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AuthGuard>
  );
};

// Public Routes Component
const PublicRoutes: React.FC = () => {
  const { isAuthenticated } = useAuth();

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return (
    <Routes>
      <Route path="/admin/login" element={<Navigate to="/login" replace />} />
      <Route path="/admin" element={<Navigate to="/login" replace />} />
      <Route path="/admin/*" element={<Navigate to="/login" replace />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
};

// Main App Component
const AppContent: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return isAuthenticated ? <ProtectedRoutes /> : <PublicRoutes />;
};

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AppVersionWatcher />
        <AuthProvider>
          <Router>
            <DocumentTitle />
            <div className="App">
              <AppContent />
              <Toaster
                position="top-right"
                containerStyle={{
                  top: 67,
                }}
                toastOptions={{
                  duration: 4000,
                  style: {
                    background: '#363636',
                    color: '#fff',
                  },
                  success: {
                    duration: 3000,
                    iconTheme: {
                      primary: '#10b981',
                      secondary: '#fff',
                    },
                  },
                  error: {
                    duration: 5000,
                    iconTheme: {
                      primary: '#ef4444',
                      secondary: '#fff',
                    },
                  },
                }}
              />
            </div>
          </Router>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
